/**
 * Single Axios instance used by every page and hook.
 *
 * Auth flow:
 *   • Access token kept in memory (set by AuthContext via setAccessToken).
 *   • Every request gets `Authorization: Bearer <access>` automatically.
 *   • On 401 we transparently call POST /auth/refresh (cookie does the work),
 *     update the in-memory token, and retry the original request — once.
 *   • If refresh itself returns 401 → emit an "auth:logout" event so the
 *     AuthContext can drop user state and the router can bounce to /login.
 *
 * `withCredentials: true` is required so the refresh-token cookie travels
 * with /auth/refresh.
 */
import axios, {
	AxiosError,
	AxiosInstance,
	AxiosRequestConfig,
	InternalAxiosRequestConfig,
} from 'axios';

/**
 * Empty default baseURL means requests go to the same origin as the FE dev
 * server, which then proxies them to the backend per `client/package.json#proxy`.
 * This sidesteps CORS AND keeps SameSite=Lax cookies working in dev.
 *
 * Setting REACT_APP_API_URL to a different origin breaks the auth flow in dev
 * because SameSite=Lax cookies aren't sent on cross-origin XHR — every page
 * reload would log the user out.
 */
const BASE_URL = process.env.REACT_APP_API_URL || '';

if (
	BASE_URL &&
	typeof window !== 'undefined' &&
	!BASE_URL.startsWith(window.location.origin)
) {
	// eslint-disable-next-line no-console
	console.warn(
		`[api] REACT_APP_API_URL=${BASE_URL} is a different origin than the page (${window.location.origin}).\n` +
			'In dev this prevents the refresh-token cookie from being sent (SameSite=Lax) and causes auth to fail on reload.\n' +
			'Either remove REACT_APP_API_URL from your .env to use the CRA proxy, or run the API on the same origin.'
	);
}

let accessToken: string | null = null;
export const setAccessToken = (t: string | null) => {
	accessToken = t;
};
export const getAccessToken = () => accessToken;

/** Auth events — AuthContext listens for these to react to silent state changes. */
type AuthEvent = 'auth:logout' | 'auth:refreshed';
type Listener = (e: AuthEvent, payload?: unknown) => void;
const listeners = new Set<Listener>();
export const onAuthEvent = (fn: Listener) => {
	listeners.add(fn);
	return () => listeners.delete(fn);
};
const emit = (e: AuthEvent, payload?: unknown) => {
	listeners.forEach((l) => l(e, payload));
};

export const api: AxiosInstance = axios.create({
	baseURL: BASE_URL,
	withCredentials: true,
	timeout: 20_000,
});

// --- request interceptor: attach access token + locale hint -----------------
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
	config.headers = config.headers || ({} as any);
	if (accessToken) {
		(config.headers as any).Authorization = `Bearer ${accessToken}`;
	}
	// `X-Locale` tells the server which language to render validation /
	// error messages in. We read from localStorage (where i18next-browser-
	// languagedetector also writes) so the value is up-to-date even before
	// the React tree has mounted. Anything other than en/ar collapses on
	// the server.
	try {
		const stored =
			typeof window !== 'undefined'
				? window.localStorage.getItem('sparts.lang')
				: null;
		if (stored) {
			(config.headers as any)['X-Locale'] = stored;
		}
	} catch (_) {
		// localStorage can throw in incognito on some browsers — non-fatal.
	}
	return config;
});

// --- single-flight refresh shared by everything that needs to refresh -------
//
// AuthContext (on page load) AND the response interceptor (on 401) both need
// to call /auth/refresh. If they fire concurrently with the same cookie, the
// first call rotates the token and the second sees a now-revoked token and
// triggers backend theft detection. We expose a single promise that BOTH
// callers await, so only one /auth/refresh is ever in flight.
let refreshPromise: Promise<RefreshResult> | null = null;

export interface RefreshResult {
	accessToken: string | null;
	user: any | null;
}

export function refreshSession(): Promise<RefreshResult> {
	if (refreshPromise) return refreshPromise;
	refreshPromise = (async () => {
		try {
			const res = await axios.post(
				`${BASE_URL}/auth/refresh`,
				{},
				{ withCredentials: true }
			);
			const newToken = (res.data as any)?.accessToken || null;
			const user = (res.data as any)?.user || null;
			if (newToken) {
				accessToken = newToken;
				emit('auth:refreshed', { accessToken: newToken, user });
			} else {
				emit('auth:logout');
			}
			return { accessToken: newToken, user };
		} catch {
			emit('auth:logout');
			return { accessToken: null, user: null };
		} finally {
			// Free the slot so subsequent expirations can refresh again later.
			refreshPromise = null;
		}
	})();
	return refreshPromise;
}

// --- response interceptor: refresh on 401 -----------------------------------
api.interceptors.response.use(
	(r) => r,
	async (error: AxiosError) => {
		const original = error.config as
			| (AxiosRequestConfig & { _retried?: boolean })
			| undefined;

		// Network/timeout errors etc. — pass through.
		if (!error.response || !original) return Promise.reject(error);

		const status = error.response.status;
		const url = original.url || '';

		// Don't try to refresh on the refresh endpoint itself or on login/logout.
		const isAuthEndpoint =
			url.includes('/auth/refresh') ||
			url.includes('/auth/login') ||
			url.includes('/auth/logout');

		if (status === 401 && !original._retried && !isAuthEndpoint) {
			original._retried = true;
			const { accessToken: newToken } = await refreshSession();

			if (!newToken) {
				return Promise.reject(error); // emit('auth:logout') already fired
			}
			original.headers = original.headers || {};
			(original.headers as any).Authorization = `Bearer ${newToken}`;
			return api.request(original);
		}

		return Promise.reject(error);
	}
);

/**
 * Convenience helpers — keep call sites tidy.
 */
export async function apiGet<T>(url: string, config?: AxiosRequestConfig) {
	const r = await api.get<T>(url, config);
	return r.data;
}
export async function apiPost<T, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig) {
	const r = await api.post<T>(url, body, config);
	return r.data;
}
export async function apiPatch<T, B = unknown>(url: string, body?: B, config?: AxiosRequestConfig) {
	const r = await api.patch<T>(url, body, config);
	return r.data;
}
export async function apiDelete<T>(url: string, config?: AxiosRequestConfig) {
	const r = await api.delete<T>(url, config);
	return r.data;
}
